// @ts-nocheck: Legacy page using mock data. Will be rewritten to use real API services.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Download,
  CheckCircle2,
  Filter,
  ArrowUpDown,
  Eye,
  CheckCircle,
  ArrowUp,
  ArrowDown,
  Search,
  Minus,
  Clock,
  ShieldCheck,
  ShieldX,
} from 'lucide-react';
import { mockKYCSubmissions, mockKYCStats } from '@/mocks/kyc.mock';
import { KYCStatus } from '@apptypes/legacy';
import { formatDistanceToNow } from 'date-fns';

/**
 * KYC Queue Page - matches internal-kyc-queue.html design exactly
 * Displays all KYC submissions with filtering, stats, and actions
 */

export const KYCQueuePage = () => {
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedPriority, setSelectedPriority] = useState<string>('all');
  const [selectedDate, setSelectedDate] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());

  // Filter submissions based on selected filters
  const filteredSubmissions = mockKYCSubmissions.filter((submission) => {
    // Status filter
    if (selectedStatus !== 'all') {
      if (selectedStatus === 'pending' && submission.status !== KYCStatus.SUBMITTED) return false;
      if (selectedStatus === 'in-review' && submission.status !== KYCStatus.UNDER_REVIEW)
        return false;
      if (selectedStatus === 'approved' && submission.status !== KYCStatus.APPROVED) return false;
      if (selectedStatus === 'rejected' && submission.status !== KYCStatus.REJECTED) return false;
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        submission.businessName.toLowerCase().includes(query) ||
        submission.merchantEmail.toLowerCase().includes(query) ||
        submission.id.toLowerCase().includes(query)
      );
    }

    return true;
  });

  // Get status badge classes: design system pill tokens
  const getStatusBadgeClass = (status: KYCStatus) => {
    switch (status) {
      case KYCStatus.SUBMITTED:
        return 'rounded-full bg-[var(--state-warning-bg)] text-[var(--state-warning)] text-[11px] font-semibold px-2.5 py-1';
      case KYCStatus.UNDER_REVIEW:
        return 'rounded-full bg-[var(--bg-elevated)] text-[var(--text-accent)] text-[11px] font-semibold px-2.5 py-1';
      case KYCStatus.APPROVED:
        return 'rounded-full bg-[var(--state-success-bg)] text-[var(--state-success)] text-[11px] font-semibold px-2.5 py-1';
      case KYCStatus.REJECTED:
        return 'rounded-full bg-[var(--state-danger-bg)] text-[var(--state-danger)] text-[11px] font-semibold px-2.5 py-1';
      default:
        return 'rounded-full bg-[var(--bg-base)] text-[var(--text-muted)] text-[11px] font-semibold px-2.5 py-1';
    }
  };

  // Get status text
  const getStatusText = (status: KYCStatus) => {
    switch (status) {
      case KYCStatus.SUBMITTED:
        return 'Pending Review';
      case KYCStatus.UNDER_REVIEW:
        return 'In Review';
      case KYCStatus.APPROVED:
        return 'Approved';
      case KYCStatus.REJECTED:
        return 'Rejected';
      default:
        return status;
    }
  };

  // Get priority badge (mock - based on submission time)
  const getPriority = (submittedAt: string) => {
    const hoursSinceSubmission =
      (Date.now() - new Date(submittedAt).getTime()) / (1000 * 60 * 60);
    if (hoursSinceSubmission < 6) return 'high';
    if (hoursSinceSubmission < 24) return 'medium';
    return 'low';
  };

  // Priority badge: design system tokens
  const getPriorityBadgeClass = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'rounded-full bg-[var(--state-danger-bg)] text-[var(--state-danger)] text-[11px] font-semibold px-2.5 py-1';
      case 'medium':
        return 'rounded-full bg-[var(--state-warning-bg)] text-[var(--state-warning)] text-[11px] font-semibold px-2.5 py-1';
      case 'low':
        return 'rounded-full bg-[var(--bg-elevated)] text-[var(--text-accent)] text-[11px] font-semibold px-2.5 py-1';
      default:
        return 'rounded-full bg-[var(--bg-base)] text-[var(--text-muted)] text-[11px] font-semibold px-2.5 py-1';
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'high':
        return <ArrowUp className="w-3.5 h-3.5" strokeWidth={1.5} />;
      case 'medium':
        return <Minus className="w-3.5 h-3.5" strokeWidth={1.5} />;
      case 'low':
        return <ArrowDown className="w-3.5 h-3.5" strokeWidth={1.5} />;
      default:
        return null;
    }
  };

  // Get avatar initials
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((word) => word[0])
      .join('')
      .substring(0, 2)
      .toUpperCase();
  };

  // Count complete documents
  const getDocumentStatus = (documents: any[]) => {
    const total = documents.length;
    const complete = documents.filter((d) => d.status === 'VERIFIED').length;
    return `${complete}/${total} Complete`;
  };

  // Toggle item selection
  const toggleSelection = (id: string) => {
    const newSelection = new Set(selectedItems);
    if (newSelection.has(id)) {
      newSelection.delete(id);
    } else {
      newSelection.add(id);
    }
    setSelectedItems(newSelection);
  };

  // Toggle all items
  const toggleSelectAll = () => {
    if (selectedItems.size === filteredSubmissions.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(filteredSubmissions.map((s) => s.id)));
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg-base)] p-8">
      {/* Page headline */}
      <div className="mb-8">
        <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-1">
          Compliance
        </p>
        <h1 className="font-display text-[32px] font-medium tracking-[-0.02em] text-[var(--text-primary)]">
          KYC Verification Queue
        </h1>
      </div>

      {/* Stats Strip: 4 mini-stat cards */}
      <div className="grid grid-cols-4 gap-5 mb-6">
        {/* Pending Review */}
        <div className="rounded-3xl bg-white shadow-soft p-6">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
                Pending Review
              </p>
              <p className="font-display text-[36px] font-medium tracking-[-0.03em] text-[var(--text-primary)] mt-1">
                {mockKYCStats.pending}
              </p>
            </div>
            <div className="rounded-2xl p-2 bg-[var(--bg-base)]">
              <Clock className="w-5 h-5 text-[var(--state-warning)]" strokeWidth={1.5} />
            </div>
          </div>
          <div className="flex items-center gap-1 text-[12px] text-[var(--state-success)] font-medium mt-3">
            <ArrowUp className="w-3.5 h-3.5" strokeWidth={1.5} />
            {mockKYCStats.newToday} new today
          </div>
        </div>

        {/* In Review */}
        <div className="rounded-3xl bg-white shadow-soft p-6">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
                In Review
              </p>
              <p className="font-display text-[36px] font-medium tracking-[-0.03em] text-[var(--text-primary)] mt-1">
                {mockKYCStats.underReview}
              </p>
            </div>
            <div className="rounded-2xl p-2 bg-[var(--bg-base)]">
              <Filter className="w-5 h-5 text-[var(--text-accent)]" strokeWidth={1.5} />
            </div>
          </div>
          <p className="text-[12px] text-[var(--text-muted)] mt-3">Currently being reviewed</p>
        </div>

        {/* Approved This Week */}
        <div className="rounded-3xl bg-white shadow-soft p-6">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
                Approved This Week
              </p>
              <p className="font-display text-[36px] font-medium tracking-[-0.03em] text-[var(--text-primary)] mt-1">
                {mockKYCStats.approvedThisWeek}
              </p>
            </div>
            <div className="rounded-2xl p-2 bg-[var(--bg-base)]">
              <ShieldCheck className="w-5 h-5 text-[var(--state-success)]" strokeWidth={1.5} />
            </div>
          </div>
          <div className="flex items-center gap-1 text-[12px] text-[var(--state-success)] font-medium mt-3">
            <ArrowUp className="w-3.5 h-3.5" strokeWidth={1.5} />
            +12% from last week
          </div>
        </div>

        {/* Rejected This Week */}
        <div className="rounded-3xl bg-white shadow-soft p-6">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
                Rejected This Week
              </p>
              <p className="font-display text-[36px] font-medium tracking-[-0.03em] text-[var(--text-primary)] mt-1">
                {mockKYCStats.rejectedThisWeek}
              </p>
            </div>
            <div className="rounded-2xl p-2 bg-[var(--bg-base)]">
              <ShieldX className="w-5 h-5 text-[var(--state-danger)]" strokeWidth={1.5} />
            </div>
          </div>
          <div className="flex items-center gap-1 text-[12px] text-[var(--state-danger)] font-medium mt-3">
            <ArrowDown className="w-3.5 h-3.5" strokeWidth={1.5} />
            -40% from last week
          </div>
        </div>
      </div>

      {/* Filters Row */}
      <div className="rounded-3xl bg-white shadow-soft p-6 flex flex-wrap gap-3 items-center mb-6">
        {/* Header row within filters card */}
        <div className="w-full flex items-center justify-between mb-1">
          <h2 className="text-[17px] font-semibold text-[var(--text-primary)]">
            Filter Submissions
          </h2>
          <div className="flex gap-3">
            <button className="inline-flex items-center gap-2 rounded-full border border-[var(--border-default)] bg-white px-5 py-2.5 text-[14px] font-medium text-[var(--text-primary)] transition-all duration-200 hover:border-[var(--border-strong)]">
              <Download className="w-4 h-4" strokeWidth={1.5} />
              Export CSV
            </button>
            <button className="inline-flex items-center gap-2 rounded-full bg-[var(--gradient-to)] px-5 py-2.5 text-[14px] font-semibold text-white transition-all duration-200 hover:brightness-110 hover:shadow-[0_12px_32px_rgba(124,179,66,0.45)] active:brightness-95">
              <CheckCircle2 className="w-4 h-4" strokeWidth={1.5} />
              Bulk Approve
            </button>
          </div>
        </div>

        {/* Bulk action bar: shown when rows are selected */}
        {selectedItems.size > 0 && (
          <div className="w-full rounded-2xl bg-[var(--brand-forest)] px-5 py-3 flex items-center gap-4 text-white text-[14px]">
            <CheckCircle className="w-4 h-4 text-[var(--text-accent)]" strokeWidth={1.5} />
            <span className="font-semibold">{selectedItems.size} selected</span>
            <button className="ml-auto inline-flex items-center gap-2 rounded-full bg-[var(--gradient-to)] px-4 py-1.5 text-[13px] font-semibold text-white hover:bg-[var(--text-accent)] transition-colors">
              <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={1.5} />
              Approve Selected
            </button>
            <button
              onClick={() => setSelectedItems(new Set())}
              className="inline-flex items-center gap-1.5 rounded-full border border-white/20 px-4 py-1.5 text-[13px] font-semibold text-white/80 hover:border-white/40 hover:text-white transition-colors"
            >
              Clear
            </button>
          </div>
        )}

        {/* Filter controls */}
        <select
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
          className="rounded-2xl border border-[var(--border-default)] bg-white px-4 py-2.5 text-[14px] text-[var(--text-primary)] focus:border-[var(--border-strong)] transition-colors cursor-pointer"
        >
          <option value="all">All Statuses</option>
          <option value="pending">Pending Review</option>
          <option value="in-review">In Review</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>

        <select
          value={selectedPriority}
          onChange={(e) => setSelectedPriority(e.target.value)}
          className="rounded-2xl border border-[var(--border-default)] bg-white px-4 py-2.5 text-[14px] text-[var(--text-primary)] focus:border-[var(--border-strong)] transition-colors cursor-pointer"
        >
          <option value="all">All Priorities</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>

        <select
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          className="rounded-2xl border border-[var(--border-default)] bg-white px-4 py-2.5 text-[14px] text-[var(--text-primary)] focus:border-[var(--border-strong)] transition-colors cursor-pointer"
        >
          <option value="all">All Time</option>
          <option value="today">Today</option>
          <option value="week">This Week</option>
          <option value="month">This Month</option>
        </select>

        <div className="flex-1 max-w-sm relative">
          <Search
            className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-muted)]"
            strokeWidth={1.5}
          />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by business name, email, or ID..."
            className="w-full pl-10 pr-4 py-3 rounded-2xl border border-[var(--border-default)] bg-white text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors"
          />
        </div>
      </div>

      {/* Table Card */}
      <div className="rounded-3xl bg-white shadow-soft overflow-hidden">
        {/* Table top bar */}
        <div className="px-6 py-5 border-b border-[var(--border-subtle)] flex items-center justify-between">
          <h2 className="text-[17px] font-semibold text-[var(--text-primary)]">
            KYC Submissions ({filteredSubmissions.length})
          </h2>
          <div className="flex gap-2">
            <button className="w-8 h-8 rounded-full flex items-center justify-center text-[var(--text-muted)] hover:bg-[var(--bg-base)] hover:text-[var(--text-primary)] transition-colors">
              <Filter className="w-4 h-4" strokeWidth={1.5} />
            </button>
            <button className="w-8 h-8 rounded-full flex items-center justify-center text-[var(--text-muted)] hover:bg-[var(--bg-base)] hover:text-[var(--text-primary)] transition-colors">
              <ArrowUpDown className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[var(--border-subtle)] bg-[var(--bg-base)]">
                <th className="w-12 px-6 py-4">
                  <input
                    type="checkbox"
                    checked={
                      selectedItems.size === filteredSubmissions.length &&
                      filteredSubmissions.length > 0
                    }
                    onChange={toggleSelectAll}
                    className="w-4 h-4 rounded border border-[var(--border-default)] cursor-pointer accent-[var(--brand-leaf)]"
                  />
                </th>
                <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] text-left">
                  Business
                </th>
                <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] text-left">
                  Submitted
                </th>
                <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] text-left">
                  Status
                </th>
                <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] text-left">
                  Priority
                </th>
                <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] text-left">
                  Documents
                </th>
                <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] text-left">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredSubmissions.map((submission) => {
                const priority = getPriority(submission.submittedAt);
                const isSelected = selectedItems.has(submission.id);
                return (
                  <tr
                    key={submission.id}
                    className={`border-b border-[var(--border-subtle)] last:border-0 hover:bg-[var(--bg-base)] transition-colors duration-150 ${
                      isSelected ? 'bg-[var(--bg-elevated)]' : ''
                    }`}
                  >
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelection(submission.id)}
                        className="w-4 h-4 rounded border border-[var(--border-default)] cursor-pointer accent-[var(--brand-leaf)]"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        {/* Flat avatar: no gradient */}
                        <div className="w-9 h-9 rounded-2xl bg-[var(--bg-elevated)] flex items-center justify-center text-[var(--text-accent)] text-[13px] font-semibold flex-shrink-0">
                          {getInitials(submission.businessName)}
                        </div>
                        <div className="min-w-0">
                          <div className="text-[14px] font-semibold text-[var(--text-primary)] truncate">
                            {submission.businessName}
                          </div>
                          <div className="text-[13px] text-[var(--text-muted)] truncate">
                            {submission.merchantEmail}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-[14px] text-[var(--text-secondary)]">
                      {formatDistanceToNow(new Date(submission.submittedAt), { addSuffix: true })}
                    </td>
                    <td className="px-6 py-4">
                      <span className={getStatusBadgeClass(submission.status)}>
                        {getStatusText(submission.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex items-center gap-1 ${getPriorityBadgeClass(priority)}`}
                      >
                        {getPriorityIcon(priority)}
                        {priority.charAt(0).toUpperCase() + priority.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-[14px] text-[var(--text-secondary)]">
                      {getDocumentStatus(submission.documents)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        <Link
                          to={`/kyc/${submission.id}`}
                          className="inline-flex items-center gap-1.5 rounded-full border border-[var(--border-subtle)] px-3 py-1.5 text-[13px] font-semibold text-[var(--text-primary)] hover:border-[var(--border-default)] hover:bg-[var(--bg-base)] transition-all"
                        >
                          <Eye className="w-3.5 h-3.5" strokeWidth={1.5} />
                          Review
                        </Link>
                        <button className="inline-flex items-center gap-1.5 rounded-full border border-[var(--border-subtle)] px-3 py-1.5 text-[13px] font-semibold text-[var(--state-success)] hover:border-[var(--border-default)] hover:bg-[var(--state-success-bg)] transition-all">
                          <CheckCircle className="w-3.5 h-3.5" strokeWidth={1.5} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-6 py-5 border-t border-[var(--border-subtle)] flex items-center justify-between">
          <div className="text-[14px] text-[var(--text-muted)]">
            Showing{' '}
            <span className="font-semibold text-[var(--text-primary)]">1–{filteredSubmissions.length}</span>{' '}
            of{' '}
            <span className="font-semibold text-[var(--text-primary)]">{filteredSubmissions.length}</span>{' '}
            submissions
          </div>
          <div className="flex gap-2">
            <button
              disabled
              className="rounded-full border border-[var(--border-subtle)] px-4 py-2 text-[13px] font-medium text-[var(--text-muted)] cursor-not-allowed"
            >
              Previous
            </button>
            <button className="rounded-full bg-[var(--brand-forest)] px-4 py-2 text-[13px] font-semibold text-white">
              1
            </button>
            <button className="rounded-full border border-[var(--border-subtle)] px-4 py-2 text-[13px] font-medium text-[var(--text-secondary)] hover:border-[var(--border-default)] hover:bg-[var(--bg-base)] transition-colors">
              2
            </button>
            <button className="rounded-full border border-[var(--border-subtle)] px-4 py-2 text-[13px] font-medium text-[var(--text-secondary)] hover:border-[var(--border-default)] hover:bg-[var(--bg-base)] transition-colors">
              3
            </button>
            <button className="rounded-full border border-[var(--border-subtle)] px-4 py-2 text-[13px] font-medium text-[var(--text-secondary)] hover:border-[var(--border-default)] hover:bg-[var(--bg-base)] transition-colors">
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};